/**
 * 🌟 Google Opal Light Theme
 * Tema inspirado en Google Opal con paleta moderna y profesional
 *
 * @description Tema claro con colores suaves, excelente contraste y
 * diseño limpio que refleja las mejores prácticas de Google Material Design
 */

export const googleOpalLightTheme = {
  name: 'Google Opal Light',
  mode: 'light',
  
  // 🎨 Paleta de colores principal
  palette: {
    primary: {
      main: '#1976D2',        // Azul Google confiable
      light: '#42A5F5',       // Azul claro vibrante
      dark: '#1565C0',        // Azul oscuro profundo
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#9C27B0',        // Púrpura elegante
      light: '#BA68C8',       // Púrpura suave
      dark: '#7B1FA2',        // Púrpura profundo
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      dark: '#C62828'
    },
    warning: {
      main: '#ED6C02',
      light: '#FF9800',
      dark: '#E65100'
    },
    info: {
      main: '#0288D1',
      light: '#03DAC6',
      dark: '#01579B'
    },
    success: {
      main: '#2E7D32',
      light: '#4CAF50',
      dark: '#1B5E20'
    },
    
    // 🖼️ Fondos y superficies
    background: {
      default: '#FAFBFC',      // Fondo principal ultra suave
      paper: '#FFFFFF',        // Tarjetas y superficies
      elevated: '#F8F9FA'      // Elementos elevados
    },
    
    // 📝 Textos con excelente legibilidad
    text: {
      primary: '#111827',      // Negro suave para títulos
      secondary: '#6B7280',    // Gris medio para subtextos
      disabled: '#9CA3AF',     // Gris claro para elementos deshabilitados
      hint: '#D1D5DB'         // Gris muy claro para hints
    },
    
    // 🎯 Colores de acción
    action: {
      active: '#1976D2',
      hover: 'rgba(25, 118, 210, 0.04)',
      selected: 'rgba(25, 118, 210, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)'
    },
    
    // 📏 Divisores
    divider: 'rgba(0, 0, 0, 0.08)',

    // 🦅 Colores de marca AIFlock
    brand: {
      main: '#9333EA',          // AIFlock purple principal
      light: '#A855F7',         // Purple claro
      dark: '#7C3AED',          // Purple oscuro
      contrastText: '#FFFFFF'
    },

    // 🎨 Escala de grises
    grey: {
      50: '#F9FAFB',            // Gris muy claro (suggestion cards)
      100: '#F3F4F6',           // Gris claro (hover state)
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'
    }
  },

  // ✨ Efectos visuales (shadows, overlays, hover states)
  effects: {
    // Sombras con diferentes intensidades
    shadows: {
      subtle: '0px 1px 4px rgba(0, 0, 0, 0.1)',
      medium: '0px 2px 8px rgba(0, 0, 0, 0.15)',
      strong: '0px 4px 20px rgba(0, 0, 0, 0.2)',
      brand: '0 4px 20px rgba(25, 118, 210, 0.2)',      // Sombra con color primario
      brandMedium: '0 2px 8px rgba(25, 118, 210, 0.10)',
      brandStrong: '0 4px 16px rgba(25, 118, 210, 0.16)'
    },

    // Estados hover
    hover: {
      background: 'rgba(0, 0, 0, 0.04)',                // Hover universal light mode
      primary: 'rgba(25, 118, 210, 0.08)',              // Hover con color primario
      card: '0px 1px 4px rgba(0, 0, 0, 0.1)',           // Sombra en hover de cards
      cardBackground: 'rgba(25, 118, 210, 0.04)'        // Background hover de cards
    },

    // Overlays
    overlay: {
      loading: 'rgba(0, 0, 0, 0.7)',                    // Overlay oscuro para loading
      modal: 'rgba(0, 0, 0, 0.5)',                      // Overlay para modales
      subtle: 'rgba(0, 0, 0, 0.3)'                      // Overlay sutil
    },

    // Estados de advertencia (warning)
    warning: {
      background: 'rgba(237, 108, 2, 0.15)',            // Background warning
      hover: 'rgba(237, 108, 2, 0.2)',                  // Hover warning
      border: 'rgba(237, 108, 2, 0.3)'                  // Border warning
    }
  },

  // 🎛️ Configuración específica por sección
  sections: {
    // 💬 Chat Interface
    chat: {
      background: '#FAFBFC',
      messageBg: '#FFFFFF',
      inputBg: '#F8F9FA',
      userMessageBg: '#E3F2FD',
      botMessageBg: '#FFFFFF',
      border: '#E5E7EB',
      shadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    },

    // 🎨 Flow Editor - LA ESTRELLA DEL SHOW
    editor: {
      canvasBg: '#FAFBFC',          // ⭐ CAMBIO CLAVE: Fondo claro Google Opal
      nodeBg: '#FFFFFF',            // Nodos blancos limpios
      nodeBorder: '#E5E7EB',        // Bordes suaves
      nodeSelected: '#1976D2',      // Azul para selección
      nodeHover: '#F3F4F6',         // Hover sutil
      toolbarBg: 'rgba(255, 255, 255, 0.95)', // Toolbar glassmorphism
      toolbarShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      connectionDefault: '#9CA3AF',  // Conexiones grises
      connectionActive: '#1976D2',   // Conexiones activas azules
      miniMapBg: 'rgba(248, 249, 250, 0.9)',
      gridColor: '#DCE0E6'          // ~1,28:1 sobre canvasBg: presente pero sin competir con los nodos
    },

    // 👥 Roles Editor
    roles: {
      cardBg: '#FFFFFF',
      sidebarBg: '#F8F9FA',
      accent: '#1976D2',
      border: '#E5E7EB',
      hover: '#F3F4F6',
      selected: '#E3F2FD'
    },

    // ⚙️ Settings
    settings: {
      panelBg: '#FAFBFC',
      cardBg: '#FFFFFF',
      border: '#E5E7EB',
      accent: '#1976D2'
    },

    // 🎯 Onboarding
    onboarding: {
      bg: '#FAFBFC',
      cardBg: '#FFFFFF',
      accent: '#1976D2',
      progress: '#42A5F5'
    }
  },

  // 🎭 Tipografía moderna
  typography: {
    fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    
    h1: { fontSize: '2.5rem', fontWeight: 600, color: '#111827', lineHeight: 1.2 },
    h2: { fontSize: '2rem', fontWeight: 600, color: '#111827', lineHeight: 1.3 },
    h3: { fontSize: '1.75rem', fontWeight: 500, color: '#111827', lineHeight: 1.4 },
    h4: { fontSize: '1.5rem', fontWeight: 500, color: '#374151', lineHeight: 1.4 },
    h5: { fontSize: '1.25rem', fontWeight: 500, color: '#374151', lineHeight: 1.5 },
    h6: { fontSize: '1.125rem', fontWeight: 500, color: '#4B5563', lineHeight: 1.5 },
    
    body1: { fontSize: '1rem', color: '#374151', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', color: '#6B7280', lineHeight: 1.6 },
    caption: { fontSize: '0.75rem', color: '#9CA3AF', lineHeight: 1.5 },
    button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' }
  },

  // ✨ Sombras elegantes
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
    
    // Sombras específicas para componentes
    card: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
    modal: '0 25px 50px rgba(0, 0, 0, 0.15)',
    tooltip: '0 4px 6px rgba(0, 0, 0, 0.1)'
  },

  // 📏 Espaciado consistente
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    xxl: '3rem'       // 48px
  },

  // 🎭 Animaciones suaves
  animations: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    
    // Animaciones específicas
    fadeIn: 'fadeIn 200ms ease-out',
    slideUp: 'slideUp 250ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'bounce 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },

  // 📱 Breakpoints responsivos
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
  },

  // 🎨 Overrides de componentes MUI
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#FAFBFC !important',
          color: '#374151',
          fontFamily: '"Segoe UI", "Selawik", system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        // ✅ ARREGLO: Asegurar que todo el HTML tenga fondo claro
        html: {
          backgroundColor: '#FAFBFC !important'
        },
        // ✅ ARREGLO: Root container también claro
        '#root': {
          backgroundColor: '#FAFBFC !important',
          minHeight: '100dvh'
        },
        // ✅ ARREGLO: Cualquier div principal
        'div[data-testid="main-container"], .main-container, .MuiBox-root': {
          backgroundColor: 'inherit'
        }
      }
    },
    
    MuiPaper: {
      styleOverrides: {
        root: {
          // Don't hardcode backgroundColor - let components use sx prop or default to theme.palette.background.paper
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          borderRadius: 8
        }
      }
    },
    
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          },
          // ✅ ARREGLO: Mejor contraste para botones deshabilitados en modo claro
          '&.Mui-disabled': {
            backgroundColor: 'rgba(229, 231, 235, 0.8)', // Gris claro visible
            color: 'rgba(107, 114, 128, 0.8)', // Texto gris oscuro con buen contraste
            border: '1px solid rgba(229, 231, 235, 0.6)'
          }
        },
        contained: {
          // ✅ ARREGLO: Botones contained deshabilitados
          '&.Mui-disabled': {
            backgroundColor: 'rgba(229, 231, 235, 0.6)', // Fondo gris claro
            color: 'rgba(75, 85, 99, 0.7)', // Texto oscuro legible
            backgroundImage: 'none'
          }
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
          color: '#FFFFFF',
          '&:hover': {
            background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)'
          }
        }
      }
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }
      }
    }
  }
};
